import { z } from "zod";
import { Input } from "src/components/common/form/input";
import Button from "react-bootstrap/Button";
import { usePostParentMutation } from "src/services/react-query-apis/post-parent";
import { usePutParentMutation } from "src/services/react-query-apis/put-parent";
import { useParentMembersListQuery } from "src/services/react-query-apis/get-parent-members-list";
import { swalSuccess } from "src/utils/swalHelpers";
import { Form } from "../common/form";
import { useState } from "react";
import { RSelect } from "../common/form/select";

const parentSchema = {
  name: z.string().min(1, "名前を入力してください"),
  phone: z
    .string()
    .regex(/^[0-9]+$/, {
      message: "電話番号は半角数字のみで入力してください",
    })
    .length(11, "電話番号を11桁で入力してください")
    .optional()
    .or(z.literal(""))
    .transform(val => (val === "" ? null : val)),
  zipcode: z
    .string()
    .min(7, "7文字で入力してください")
    .max(7, "7文字で入力してください")
    .optional()
    .or(z.literal(""))
    .transform(val => (val === "" ? null : val)),
  address1: z
    .string()
    .optional()
    .or(z.literal(""))
    .transform(val => (val === "" ? null : val)),
  address2: z
    .string()
    .optional()
    .or(z.literal(""))
    .transform(val => (val === "" ? null : val)),
  login_id: z
    .string()
    .min(6, "6文字以上で入力してください")
    .regex(/^[A-Za-z0-9_]+$/, {
      message: "ログインIDは半角英数字とアンダースコアのみで入力してください。",
    }),
  parent_members: z
    .array(z.object({ value: z.number(), label: z.string() }))
    .optional(),
};

const createSchema = {
  ...parentSchema,
  password_hash: z
    .string()
    .min(6, "6文字以上で入力してください")
    .regex(/^[A-Za-z0-9_\-?!@#$%^*]+$/, {
      message:
        "パスワードは半角英数字とアンダースコア、記号のみで入力してください。",
    }),
};

const updateSchema = parentSchema;

const formFieldArr = [
  {
    name: "name",
    type: "text",
    label: "氏名",
    placeholder: "例：田中 太郎",
  },
  {
    name: "phone",
    type: "text",
    label: "電話番号",
    placeholder: "電話番号",
  },
  {
    name: "zipcode",
    type: "text",
    label: "郵便番号",
    placeholder: "郵便番号",
  },
  {
    name: "address1",
    type: "text",
    label: "市区町村",
    placeholder: "市区町村",
  },
  {
    name: "address2",
    type: "text",
    label: "番地・物件名・部屋番号",
    placeholder: "番地・物件名・部屋番号",
  },
  {
    name: "login_id",
    type: "text",
    label: "ログインID",
    placeholder: "ご入力ください",
  },
  {
    name: "password_hash",
    type: "password",
    label: "パスワード",
    placeholder: "ご入力ください",
  },
];

export default function ParentForm({ selectedParent, closeModal, isLoading }) {
  const [searchValue, setSearchValue] = useState("");

  const mutationConfig = {
    onSuccess: async data => {
      await swalSuccess(data?.message);
      closeModal();
    },
  };

  const postParentMutation = usePostParentMutation({
    mutationConfig,
  });

  const putParentMutation = usePutParentMutation({
    mutationConfig,
  });

  const parentMembersListQuery = useParentMembersListQuery({
    search: searchValue,
  });

  const membersOptions = members => {
    return (
      members?.map(item => ({
        value: item.id,
        label: item.name,
      })) || []
    );
  };

  const defaultValues = {
    name: selectedParent?.name || "",
    phone: selectedParent?.phone || "",
    zipcode: selectedParent?.zipcode || "",
    address1: selectedParent?.address1 || "",
    address2: selectedParent?.address2 || "",
    login_id: selectedParent?.login_id || "",
    password_hash: selectedParent?.password_hash || "",
    parent_members: membersOptions(selectedParent?.members),
  };

  return (
    <Form
      onSubmit={values => {
        const payload = {
          ...values,
          parent_members: values?.parent_members?.map(item => item.value) || [],
        };

        if (selectedParent) {
          putParentMutation.mutate({
            id: selectedParent.id,
            ...payload,
          });
        } else {
          postParentMutation.mutate(payload);
        }
      }}
      schema={selectedParent ? z.object(updateSchema) : z.object(createSchema)}
      options={{ defaultValues }}
    >
      {({ setValue, register, formState, watch }) => (
        <>
          <div className="row row-cols-1">
            {formFieldArr.map(field => {
              if (selectedParent && field.name === "password_hash") {
                return null;
              }
              return (
                <div className="col mb-3" key={field.name}>
                  <Input
                    type={field.type}
                    label={field.label}
                    name={field.name}
                    placeholder={field.placeholder}
                    error={formState.errors[field.name]}
                    registration={register(field.name)}
                  />
                  {field.name === "password_hash" && (
                    <small>英数字と記号 _ - ? ! @ # $ % ^ * が使えます。</small>
                  )}
                </div>
              );
            })}
            <div className="col mb-3">
              <div>
                <label>保護者下会員</label>
                <RSelect
                  isMulti
                  isLoading={parentMembersListQuery.isPending}
                  options={
                    membersOptions(parentMembersListQuery.data?.data) || []
                  }
                  onInputChange={setSearchValue}
                  onChange={value => {
                    setValue("parent_members", value);
                  }}
                  placeholder="検索．．．"
                  value={watch("parent_members")}
                />
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <Button variant="primary" type="submit" disabled={isLoading}>
              保存
            </Button>
          </div>
        </>
      )}
    </Form>
  );
}
